import { useCallback, useEffect, useState, useMemo } from 'react';
import useFetch, { muteAll } from 'hooks/useFetch';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { selectFeatureFlag } from 'reducers/selectors';
import * as actions from '../actions';
import { selectWatchlists, selectRecurringBuys, selectPendingTopupsFlag, selectLimitStopOrders, selectFirstThreePendingAlerts, selectTradingAccount, } from '../reducers/selectors';
import * as selectors from './selectors';
import { useFakeData } from '../debug';
import { ALL_STOCKS_KEY, POSITION_FILTER_OPTIONS } from '../constants';
import { SellOrderType, } from '../types';
import { useClearBuyBlock } from './useClearBuyBlock';
const fakeWatchlists = [
    {
        id: 'watchlist-1',
        name: 'Favorites',
        emoji: '😉',
        assets: ['AAPL', 'TSLA'],
    },
];
const options = muteAll;
const usePortfolioScreenData = () => {
    const dispatch = useAppDispatch();
    const [selectedPositionFilter, setSelectedPositionFilter] = useState(POSITION_FILTER_OPTIONS[0]);
    const connectedAccount = selectors.useConnectedAccount();
    const hasPendingTopups = useAppSelector(selectPendingTopupsFlag);
    const { account: tradingAccount, isAlpaca, hasTraded, hasFunded } = useAppSelector(selectTradingAccount);
    const positions = selectors.usePositions(selectedPositionFilter.value);
    const positionsLoaded = useAppSelector(selectors.selectPositionsLoaded);
    const { queuedOrders, pendingOrders } = selectors.usePendingAndQueuedOrders();
    const watchlists = useAppSelector(selectWatchlists);
    const recurringBuys = useAppSelector(selectRecurringBuys);
    const limitStopOrders = useAppSelector(selectLimitStopOrders);
    const pendingStockAlerts = useAppSelector((state) => selectFirstThreePendingAlerts(state, ALL_STOCKS_KEY));
    const hasStockAlerts = useAppSelector((store) => selectFeatureFlag(store, 'stock_alerts').value);
    const [, , , fetchActivities] = useFetch(options);
    const [, , , fetchPositions] = useFetch(options);
    const [, , , fetchOrders] = useFetch(options);
    const [, , , fetchWatchlists] = useFetch(options);
    const [, , , fetchRecurringBuys] = useFetch(options);
    const [, , , fetch] = useFetch(options);
    useClearBuyBlock();
    const load = useCallback(() => {
        dispatch(actions.updateWealthAccounts());
        if (tradingAccount.id) {
            fetchActivities(actions.getActivities({ accountId: tradingAccount.id, paging: { page: 1, perPage: 30 } }));
            fetchPositions(actions.getPositions({ accountId: tradingAccount.id, paging: { page: 1, perPage: 10 ** 6 } }));
            // Fetch market orders
            fetchOrders(actions.getOrders({
                accountId: tradingAccount.id,
                statuses: ['queued', 'pending'],
                orderTypes: [SellOrderType.MARKET],
            }));
            if (isAlpaca) {
                fetchWatchlists(actions.getWatchlists());
                fetchRecurringBuys(actions.getRecurringBuys());
                dispatch(actions.getPatternDayTradingInfo());
                if (hasStockAlerts) {
                    dispatch(actions.getStockAlerts());
                }
                fetch(actions.getSectors());
                // Fetch limit and stop orders
                // Limit and Stop orders can be in queued or pending state
                fetchOrders(actions.getOrders({
                    accountId: tradingAccount.id,
                    statuses: ['queued', 'pending'],
                    orderTypes: [SellOrderType.LIMIT, SellOrderType.STOP],
                }));
            }
            else {
                dispatch(actions.getAutoInvest(tradingAccount.id));
            }
            fetch(actions.getPendingTopups());
            dispatch(actions.getPendingTopupsForAccountId(tradingAccount.id));
        }
    }, [
        tradingAccount.id,
        fetchPositions,
        fetchOrders,
        isAlpaca,
        hasStockAlerts,
        fetchActivities,
        fetchWatchlists,
        fetchRecurringBuys,
    ]);
    useEffect(load, [load]);
    const refresh = useCallback(() => {
        load();
    }, [load]);
    const portfolioContextData = useMemo(() => ({
        positions,
        tradingAccount,
    }), [positions, tradingAccount]);
    return [
        {
            isAlpacaAccount: tradingAccount?.broker === 'alpaca',
            showPortfolioChart: !!((tradingAccount?.availableToInvest && connectedAccount) || hasPendingTopups),
            tradingAccount,
            hasTraded,
            hasFunded,
            queuedOrders,
            pendingOrders,
            positions,
            positionsLoaded,
            watchlists: useFakeData ? fakeWatchlists : watchlists,
            recurringBuys,
            limitStopOrders,
            pendingStockAlerts,
        },
        portfolioContextData,
        refresh,
        false, // we don’t need any loading state here (by design, Portfolio doesn’t show any loading spinners)
        { selectedPositionFilter, setSelectedPositionFilter },
    ];
};
export default usePortfolioScreenData;
